import React, { useContext, useEffect, useCallback, useState, useRef } from 'react';
import BorderedButton from 'Components/Common/components/Button/BorderedButton';
import { Prompt, useHistory } from 'react-router-dom';
import { NavRightIcon } from 'assests/GmsIcons';
import IterationComponent from './IterationComponent';
import { IterationContext, IterationContextProvider, IterationContextType } from '../../contexts/IterationContext';
import { CANCEL, CREATE, EDIT } from '../../constants/CSConstants';
import CategorySelector from './CategorySelector';
import { createIterationMethod, updateIterationMethod } from '../../services/IterationServices';
import { toast } from 'react-toastify';
import CompatioLoader from 'Components/Common/components/CompatioLoader';
import ConfirmModal from 'Pages/GMS/ISR/Components/RuleComponents/CommitConfirmModal';

type IterationConfigurationProps = {
    match: {
        params: {
            categoryid?: string;
            uid?: string;
        };
    };
};

const IterationConfiguration: React.FC<IterationConfigurationProps> = (props) => {
    const { params } = props.match;
    const { categoryid: categoryId, uid } = params;
    const history = useHistory();
    const [blockedLocation, setBlockedLocation] = useState<any>(null);
    const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
    const allowNavigationRef = useRef<boolean>(false);

    const {
        industryId,
        channelKey,
        fetchInitialState,
        resetState,
        iterationPayload,
        setIterationPayload,
        isEditMode,
        loading,
        hasUnsavedChanges,
        showCancelDialog,
        setShowCancelDialog,
    } = useContext<IterationContextType>(IterationContext);

    // Browser navigation protection
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                event.preventDefault();
                event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    useEffect(() => {
        // Initialize state based on whether we're creating or editing
        fetchInitialState(uid, categoryId);

        // Cleanup function to reset state when component unmounts
        return () => {
            if (resetState) {
                resetState();
            }
        };
    }, [uid, categoryId, industryId, channelKey]);

    // Handle Prompt navigation blocking
    const handlePrompt = useCallback((location: any, action: string) => {
        if (allowNavigationRef.current) {
            return true;
        }

        if (hasUnsavedChanges) {
            setBlockedLocation(location);
            setShowCancelDialog(true);
            return false;
        }
        return true;
    }, [hasUnsavedChanges, setShowCancelDialog]);

    const handleCancelClick = useCallback(() => {
        if (hasUnsavedChanges) {
            setBlockedLocation({ pathname: "/gms/computed_substitutions/", search: "" });
            setShowCancelDialog(true);
        } else {
            history.push("/gms/computed_substitutions/");
        }
    }, [hasUnsavedChanges, setShowCancelDialog, history]);

    const handleSave = useCallback(async (): Promise<boolean> => {
        if (saveInProgress) return false;
        setSaveInProgress(true);
        try {
            if (!iterationPayload) {
                toast.error('No data to save');
                return false;
            }

            if (!iterationPayload.category_id) {
                toast.error('Please select a category');
                return false;
            }

            // Check if any iteration has empty conditions array
            const hasEmptyIterations = iterationPayload.iterations.some(iteration =>
                !iteration.conditions || iteration.conditions.length === 0
            );

            if (hasEmptyIterations) {
                toast.error('Each iteration must have at least one condition');
                return false;
            }

            // Validate attribute sorting - if it exists, it must have attribute_id and attribute_name
            const hasInvalidAttributeSorting = iterationPayload.iterations.some(iteration => {
                if (iteration.attribute_sorting && iteration.attribute_sorting.length > 0) {
                    return iteration.attribute_sorting.some(sortItem =>
                        !sortItem.attribute_id ||
                        !sortItem.attribute_name ||
                        sortItem.attribute_id.trim() === '' ||
                        sortItem.attribute_name.trim() === ''
                    );
                }
                return false;
            });

            if (hasInvalidAttributeSorting) {
                toast.error('All attribute sorting items must have attribute selected');
                return false;
            }

            // Check for missing required fields in conditions
            const hasEmptyConditions = iterationPayload.iterations.some(iteration => {
                const checkConditions = (conditions: ConditionNode[]): boolean => {
                    return conditions.some(condition => {
                        if (condition.type === 'operations') {
                            const opCondition = condition as OperationCondition;

                            // Always required fields
                            if (!opCondition.attribute_id ||
                                !opCondition.value_type ||
                                opCondition.operand === null ||
                                opCondition.operand === undefined) {
                                return true;
                            }

                            // Operator required only when value_type is NOT "Multiplier"
                            if (opCondition.value_type !== "Multiplier" && !opCondition.operator) {
                                return true;
                            }

                            return false;
                        } else if (condition.type === 'condition') {
                            const nestedCondition = condition as NestedCondition;
                            return checkConditions(nestedCondition.conditions);
                        }
                        return false;
                    });
                };
                return checkConditions(iteration.conditions);
            });

            if (hasEmptyConditions) {
                toast.error('Please fill in all required fields for conditions');
                return false;
            }

            let payloadToUpdate = iterationPayload;

            if (!isEditMode) {
                if (!industryId || !channelKey || !iterationPayload.category_id || !iterationPayload.category_name) {
                    toast.error('Missing required information for creation');
                    return false;
                }

                const createResponse = await createIterationMethod(industryId, channelKey, iterationPayload.category_id, iterationPayload.category_name);
                if (createResponse.done && createResponse?.data?.id) {
                    payloadToUpdate = {
                        ...iterationPayload,
                        id: createResponse.data.id
                    };
                    setIterationPayload(payloadToUpdate);
                } else {
                    toast.error("Failed to create iteration method");
                    return false;
                }
            }

            const response = await updateIterationMethod(payloadToUpdate);
            if (response.done) {
                toast.success(isEditMode ? "Updated iteration method successfully" : "Created iteration method successfully");
                return true;
            } else {
                toast.error(response.message || "Save failed");
                return false;
            }
        }
        finally {
            setSaveInProgress(false);
        }
    }, [iterationPayload, isEditMode, industryId, channelKey, setIterationPayload]);

    // Cancel dialog handlers
    const handleDialogCancel = useCallback(() => {
        setShowCancelDialog(false);
        setBlockedLocation(null);
    }, [setShowCancelDialog]);

    const handleDialogSave = useCallback(async () => {
        if (saveInProgress) return;
        const saveSuccess = await handleSave();
        setShowCancelDialog(false);

        if (saveSuccess) {
            allowNavigationRef.current = true;
            if (blockedLocation) {
                history.push(blockedLocation.pathname + (blockedLocation.search || ""));
                setBlockedLocation(null);
            } else {
                // Default navigation
                history.push("/gms/computed_substitutions/");
            }
        }
    }, [handleSave, setShowCancelDialog, history, blockedLocation]);

    const handleConfirmCancel = useCallback(() => {
        setShowCancelDialog(false);
        allowNavigationRef.current = true;
        if (blockedLocation) {
            history.push(blockedLocation.pathname + (blockedLocation.search || ""));
            setBlockedLocation(null);
        } else {
            history.push("/gms/computed_substitutions/");
        }
    }, [setShowCancelDialog, history, blockedLocation]);

    const handleSaveClick = useCallback(async () => {
        if (saveInProgress) return false;
        const saveSuccess = await handleSave();
        if (saveSuccess) {
            allowNavigationRef.current = true;
            history.push("/gms/computed_substitutions/");
        }
        setSaveInProgress(false);
    }, [handleSave, history]);

    // Determine page title based on mode
    const pageTitle = isEditMode === false ? CREATE : isEditMode === true ? EDIT : "";

    return (
        <>
            <Prompt
                when={hasUnsavedChanges}
                message={handlePrompt}
            />
            <div className="tw-bg-[#FAFAFC] tw-w-full" data-testid="iteration-configuration">
                {/* Breadcrumb navigation */}
                <nav aria-label="Breadcrumb" className="tw-mb-2">
                    <ol className="tw-flex tw-items-center tw-text-xs tw-text-sky-950">
                        <li className="tw-leading-relaxed">
                            <span onClick={handleCancelClick} className="tw-hover:underline tw-cursor-pointer">
                                Iteration Method
                            </span>
                        </li>
                        <li className="tw-mx-2 tw-text-gray-400">
                            <NavRightIcon />
                        </li>
                        <li className="tw-leading-relaxed tw-font-medium" aria-current="page">{pageTitle}</li>
                    </ol>
                </nav>

                {/* Main content with title and buttons */}
                <div className="tw-flex tw-flex-row tw-items-center tw-justify-between">
                    <h1
                        id="cs-title"
                        className="tw-text-left tw-text-[#091E42] tw-font-semibold tw-text-[28px] tw-my-3.5"
                        data-testid="page-title"
                    >
                        {pageTitle}
                    </h1>
                    <div className="tw-flex tw-flex-row tw-gap-3">
                        <BorderedButton
                            onClick={handleCancelClick}
                            data-testid="cancel-button"
                            disabled={saveInProgress}
                        >
                            {CANCEL}
                        </BorderedButton>
                        <button
                            onClick={handleSaveClick}
                            className="button-style tw-w-max"
                            data-testid="save-button"
                            disabled={!iterationPayload?.category_id || saveInProgress}
                        >
                            {saveInProgress ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>

            <CategorySelector
                id="category-selection"
                categoryId={categoryId}
            />

            {loading && <CompatioLoader />}
            {!loading && iterationPayload?.category_id && <IterationComponent />}

            {/* Cancel Confirmation Dialog */}
            {showCancelDialog && <ConfirmModal
                title="Unsaved Changes"
                message="You have unsaved changes to this computed substitution. What would you like to do?"
                onClose={handleDialogCancel}
                onConfirm={handleDialogSave}
                ternaryMethod={handleConfirmCancel}
                ternaryText="Discard changes"
                confirmText="Save Changes"
                cancelText="Cancel"
                saveInProgress={saveInProgress}
            />}
        </>
    );
};

const IterationConfigurationProvider = (props: any) => {
    return (
        <IterationContextProvider>
            <IterationConfiguration {...props} />
        </IterationContextProvider>
    );
};

export default IterationConfigurationProvider;